import { useTranslation } from "react-i18next";
import React, { useEffect, useState, useCallback } from 'react';
// eslint-disable-next-line no-unused-vars
import { Alert, Badge, Button, Card, Col, Collapse, Container, Input, Row, Spinner, Table } from 'reactstrap';
import AdminLayout from '../../Components/Admin/AdminLayout';
import './ConnectionsMonitor.css';
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com/api';
const minimal = {
  bg: 'transparent',
  cardBg: 'transparent',
  border: '#e2e8f0',
  textPrimary: '#111827',
  textSecondary: '#6b7280',
  primary: '#0ea5e9',
  danger: '#ef4444',
  successBg: 'rgba(22, 163, 74, 0.1)',
  successText: '#16a34a',
  warningBg: 'rgba(217, 119, 6, 0.1)',
  warningText: '#d97706',
  dangerBg: 'rgba(220, 38, 38, 0.1)',
  dangerText: '#dc2626',
  neutralBg: '#f3f4f6',
  neutralText: '#4b5563'
};

const ConnectionsMonitor = () => {
  const {
    t
  } = useTranslation();
  const [connections, setConnections] = useState([]);
  const [groupedConnections, setGroupedConnections] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState({});
  const [selectedConnections, setSelectedConnections] = useState({});
  const [selectedGroups, setSelectedGroups] = useState({});

  const toggleSelectGroup = invitationId => {
    setSelectedGroups(prev => ({ ...prev,
      [invitationId]: !prev[invitationId]
    }));
  };

  const toggleSelectAllGroups = checked => {
    setSelectedGroups(() => {
      const next = {};

      if (checked) {
        Object.keys(groupedConnections).forEach(id => {
          next[id] = true;
        });
      }

      return next;
    });
  };

  const deleteSelectedGroups = async () => {
    const toDelete = Object.keys(selectedGroups).filter(id => selectedGroups[id]);
    if (toDelete.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete all connections in the ${toDelete.length} selected group(s)?`)) return;
    const allConnectionIds = [];
    toDelete.forEach(id => {
      const group = groupedConnections[id];

      if (group && group.connections) {
        group.connections.forEach(c => allConnectionIds.push(c.id));
      }
    });
    setUpdating(prev => {
      const next = { ...prev
      };
      allConnectionIds.forEach(cid => {
        next[cid] = true;
      });
      return next;
    });

    for (const cid of allConnectionIds) {
      await deleteConnection(cid);
    }

    setSelectedGroups({});
  };

  const toggleSelectConnection = connectionId => {
    setSelectedConnections(prev => ({ ...prev,
      [connectionId]: !prev[connectionId]
    }));
  };

  const toggleSelectAllInGroup = (groupConnections, checked) => {
    setSelectedConnections(prev => {
      const next = { ...prev
      };
      groupConnections.forEach(c => {
        next[c.id] = checked;
      });
      return next;
    });
  };

  const deleteSelectedConnections = async groupConnections => {
    const toDelete = groupConnections.filter(c => selectedConnections[c.id]);
    if (toDelete.length === 0) return;
    if (!window.confirm(`Delete ${toDelete.length} selected connection(s)? This cannot be undone.`)) return; // Perform deletions

    for (const c of toDelete) {
      await deleteConnection(c.id);
    } // Clear selection for these


    setSelectedConnections(prev => {
      const next = { ...prev
      };
      toDelete.forEach(c => {
        delete next[c.id];
      });
      return next;
    });
  };

  useEffect(() => {
    fetchConnections();
  }, []);
  const groupConnectionsByInvitationId = useCallback(() => {
    const grouped = {};
    connections.forEach(connection => {
      const invitationId = connection.invitationId || 'no-invitation-id';

      if (!grouped[invitationId]) {
        grouped[invitationId] = {
          invitationId: invitationId,
          connections: [],
          status: 'unknown',
          createdAt: connection.createdAt,
          updatedAt: connection.updatedAt
        };
      }

      grouped[invitationId].connections.push(connection); // Update group status based on connection statuses

      if (connection.status === 'connected') {
        grouped[invitationId].status = 'connected';
      } else if (connection.status === 'pending' && grouped[invitationId].status !== 'connected') {
        grouped[invitationId].status = 'pending';
      } else if (connection.status === 'declined' && grouped[invitationId].status === 'unknown') {
        grouped[invitationId].status = 'declined';
      } // Update timestamps


      if (new Date(connection.createdAt) < new Date(grouped[invitationId].createdAt)) {
        grouped[invitationId].createdAt = connection.createdAt;
      }

      if (new Date(connection.updatedAt) > new Date(grouped[invitationId].updatedAt)) {
        grouped[invitationId].updatedAt = connection.updatedAt;
      }
    });
    setGroupedConnections(grouped); // Default-open the first group if none is open

    const keys = Object.keys(grouped);

    if (keys.length > 0) {
      setExpandedGroups(prev => {
        // If any is already open, keep existing state
        if (Object.values(prev || {}).some(Boolean)) return prev;
        return {
          [keys[0]]: true
        };
      });
    }
  }, [connections]);
  useEffect(() => {
    groupConnectionsByInvitationId();
  }, [connections, groupConnectionsByInvitationId]);

  const toggleGroup = invitationId => {
    setExpandedGroups(prev => {
      const isOpen = !!prev[invitationId]; // Accordion behavior: open only the clicked one

      return isOpen ? {} : {
        [invitationId]: true
      };
    });
  };

  const fetchConnections = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('HMI_AUTH_TOKEN'); // Handle both cases: API_BASE with and without /api suffix

      const baseUrl = API_BASE.endsWith('/api') ? API_BASE : `${API_BASE}/api`;
      const response = await fetch(`${baseUrl}/admin/connections/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('API Response:', data);

      if (data.success) {
        // Handle both old and new API response formats
        const connectionsData = data.data.connections || data.data || [];
        console.log('Connections data:', connectionsData); // Enrich with synthetic inverse pending record when missing

        const enriched = Array.isArray(connectionsData) ? [...connectionsData] : [];
        connectionsData.forEach(conn => {
          const status = conn.status === 'accepted' ? 'connected' : conn.status;
          const hasInverse = Boolean(conn.inverseConnectionId) || enriched.some(c => c.inverseConnectionId === conn.id);

          if (status === 'pending' && !hasInverse) {
            enriched.push({
              id: `synth_${conn.id}`,
              status: 'pending',
              inviteCode: conn.inviteCode,
              createdAt: conn.createdAt,
              updatedAt: conn.updatedAt,
              expiresAt: conn.expiresAt,
              fromUserId: conn.toUserId || null,
              toUserId: conn.fromUserId || null,
              invitationId: conn.invitationId || 'no-invitation-id',
              inverseConnectionId: conn.id,
              fromEmail: conn.toEmail || null,
              fromUsername: conn.toUsername || null,
              toEmail: conn.fromEmail || null,
              toUsername: conn.fromUsername || null,
              __synthetic: true
            });
          }
        });
        setConnections(enriched);
      } else {
        throw new Error(data.error?.message || 'Failed to fetch connections');
      }
    } catch (err) {
      console.error('Error fetching connections:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (connectionId, newStatus) => {
    try {
      setUpdating(prev => ({ ...prev,
        [connectionId]: true
      }));
      const isSynthetic = String(connectionId).startsWith('synth_');
      const apiId = isSynthetic ? connectionId.replace('synth_', '') : connectionId;

      const token = localStorage.getItem('HMI_AUTH_TOKEN');
      const baseUrl = API_BASE.endsWith('/api') ? API_BASE : `${API_BASE}/api`;
      const response = await fetch(`${baseUrl}/admin/connections/${apiId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      } // Update local state

      setConnections(prev => prev.map(conn => (conn.id === apiId || conn.id === `synth_${apiId}`) ? { ...conn,
        status: newStatus,
        updatedAt: new Date().toISOString()
      } : conn));
    } catch (error) {
      console.error('Error updating status:', error);
      window.alert(`Failed to update status: ${error.message}`);
    } finally {
      setUpdating(prev => ({ ...prev,
        [connectionId]: false
      }));
    }
  };

  const deleteConnection = async connectionId => {
    try {
      setUpdating(prev => ({ ...prev,
        [connectionId]: true
      }));
      const isSynthetic = String(connectionId).startsWith('synth_');
      const apiId = isSynthetic ? connectionId.replace('synth_', '') : connectionId;

      const token = localStorage.getItem('HMI_AUTH_TOKEN');
      const baseUrl = API_BASE.endsWith('/api') ? API_BASE : `${API_BASE}/api`;
      const response = await fetch(`${baseUrl}/admin/connections/${apiId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status !== 404) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        console.log(`Connection ${apiId} already deleted.`);
      } // Remove from local state

      setConnections(prev => prev.filter(conn => conn.id !== apiId && conn.id !== `synth_${apiId}`));
    } catch (error) {
      console.error('Error deleting connection:', error);
      window.alert(`Failed to delete connection: ${error.message}`);
    } finally {
      setUpdating(prev => ({ ...prev,
        [connectionId]: false
      }));
    }
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  const getStatusBadgeStyle = status => {
    const base = {
      padding: '5px 10px',
      borderRadius: '4px',
      fontSize: '0.85rem',
      fontWeight: '600',
      display: 'inline-block'
    };

    switch (status) {
      case 'connected':
        return { ...base,
          backgroundColor: "transparent",
          color: minimal.successText
        };

      case 'pending':
        return { ...base,
          backgroundColor: "transparent",
          color: minimal.warningText
        };

      case 'declined':
      case 'rejected':
        return { ...base,
          backgroundColor: "transparent",
          color: minimal.dangerText
        };

      case 'expired':
        return { ...base,
          backgroundColor: "transparent",
          color: minimal.neutralText
        };

      default:
        return { ...base,
          backgroundColor: "transparent",
          color: minimal.neutralText
        };
    }
  }; // Row background/text styling by status




  if (loading) {
    return <AdminLayout>
                <div style={{
        padding: 20,
        minHeight: '100vh',
        backgroundColor: "transparent",
        color: minimal.textPrimary
      }}>
                    <div className="text-center" style={{
          paddingTop: '100px'
        }}>
                        <Spinner style={{
            color: minimal.primary
          }} />
                        <p className="mt-2" style={{
            color: minimal.textSecondary
          }}>{t("Loading connections...")}</p>
                    </div>
                </div>
            </AdminLayout>;
  }

  if (error) {
    return <AdminLayout>
                <div style={{
        padding: 20,
        minHeight: '100vh',
        backgroundColor: "transparent",
        color: minimal.textPrimary
      }}>
                    <div style={{
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
                        <Alert style={{
            backgroundColor: "transparent",
            color: minimal.dangerText,
            border: `1px solid ${minimal.danger}`
          }}>
                            <h4>{t("Error")}</h4>
                            <p>{error}</p>
                            <Button style={{
              backgroundColor: "transparent",
              color: minimal.textPrimary,
              border: `1px solid ${minimal.border}`
            }} onClick={fetchConnections}>{t("Retry")}</Button>
                        </Alert>
                    </div>
                </div>
            </AdminLayout>;
  }

  return <AdminLayout>
            <div className="connections-monitor-wrapper" style={{
      padding: 20,
      minHeight: '100vh',
      backgroundColor: "transparent",
      color: minimal.textPrimary
    }}>
                <div style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
                    <h4 className="mb-4" style={{
          color: 'white',
          fontWeight: '700',
          letterSpacing: '-0.5px',
          fontSize: '1.5rem',
          textTransform: 'none'
        }}>{t("Connection Management")}</h4>
                    
                    <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: "transparent",
          padding: '15px 20px',
          borderRadius: '8px',
          border: `1px solid ${minimal.border}`,
          marginTop: '15px',
          marginBottom: '20px'
        }}>
                        <div style={{
            display: 'flex',
            alignItems: 'center'
          }}>
                            <input type="checkbox" id="checkAllGroups" checked={Object.keys(groupedConnections).length > 0 && Object.keys(groupedConnections).every(id => selectedGroups[id])} onChange={e => toggleSelectAllGroups(e.target.checked)} style={{
              cursor: 'pointer',
              transform: 'scale(1.2)'
            }} />
                            <label htmlFor="checkAllGroups" style={{
              cursor: 'pointer',
              margin: 0,
              marginLeft: '10px',
              fontWeight: '600',
              color: minimal.textPrimary
            }}>{t("Select All Groups")}</label>
                        </div>
                        {Object.values(selectedGroups).filter(Boolean).length > 0 && <Button style={{
            backgroundColor: "transparent",
            color: '#ffffff',
            border: 'none',
            fontWeight: '600'
          }} size="sm" onClick={deleteSelectedGroups}>{t("Delete Selected Groups (")}{Object.values(selectedGroups).filter(Boolean).length})
                            </Button>}
                    </div>

                    <Container className="mt-4" fluid style={{
          padding: 0
        }}>
                        <Row>
                            <Col>
                                <Card style={{
                backgroundColor: "transparent",
                border: 'none',
                boxShadow: 'none'
              }}>
                                    <div className="card-body" style={{
                  padding: 0
                }}>
                                        {Object.keys(groupedConnections).length === 0 ? <p className="text-center" style={{
                    color: minimal.textSecondary
                  }}>{t("No connections found.")}</p> : <div>
                                                {Object.entries(groupedConnections).map(([invitationId, group]) => <Card key={invitationId} className="mb-4" style={{
                      backgroundColor: "transparent",
                      border: `1px solid ${minimal.border}`,
                      borderRadius: '8px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}>
                                                        <div className="card-header" style={{
                        backgroundColor: "transparent",
                        borderBottom: `1px solid ${minimal.border}`,
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                        padding: '15px 20px'
                      }}>
                                                            <Row className="align-items-center">
                                                                <Col xs="auto" className="pr-0">
                                                                    <input type="checkbox" checked={!!selectedGroups[invitationId]} onChange={e => {
                              e.stopPropagation();
                              toggleSelectGroup(invitationId);
                            }} style={{
                              cursor: 'pointer',
                              transform: 'scale(1.2)'
                            }} />
                                                                </Col>
                                                                <Col md="4">
                                                                    <div>
                                                                        <strong>
                                                                            <span style={{
                                  backgroundColor: "transparent",
                                  color: minimal.textPrimary,
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  marginRight: '8px',
                                  border: `1px solid ${minimal.border}`,
                                  fontSize: '0.85rem'
                                }}>{t("ID:")}{invitationId.substring(0, 20)}...
                                                                            </span>
                                                                        </strong>
                                                                        <br />
                                                                        <div style={{
                                marginTop: '8px',
                                color: minimal.textPrimary,
                                fontWeight: '500',
                                fontSize: '0.95rem'
                              }}>
                                                                            {(() => {
                                  const firstConn = group.connections[0];
                                  if (!firstConn) return null;
                                  const from = firstConn.fromEmail || firstConn.fromUsername || firstConn.fromUserId || 'Unknown';
                                  const to = firstConn.toEmail || firstConn.toUsername || firstConn.toUserId || 'Unknown';
                                  return <span>{from} ↔ {to}</span>;
                                })()}
                                                                        </div>
                                                                        <small style={{
                                color: minimal.textSecondary,
                                marginTop: '4px',
                                display: 'inline-block',
                                fontWeight: '500'
                              }}>
                                                                            {group.connections.length}{t("connection")}{group.connections.length !== 1 ? 's' : ''}
                                                                        </small>
                                                                    </div>
                                                                </Col>
                                                                <Col md="3">
                                                                    <span style={getStatusBadgeStyle(group.status)}>
                                                                        {group.status}
                                                                    </span>
                                                                </Col>
                                                                <Col md="3">
                                                                    <small style={{
                              color: minimal.textSecondary,
                              fontWeight: '500'
                            }}>{t("Created:")}{formatDate(group.createdAt)}
                                                                        <br />{t("Updated:")}{formatDate(group.updatedAt)}
                                                                    </small>
                                                                </Col>
                                                                <Col md="2" className="text-right">
                                                                    <Button style={{
                              backgroundColor: '#ffffff',
                              color: minimal.danger,
                              border: `1px solid ${minimal.danger}`,
                              marginRight: '10px',
                              fontSize: '0.85rem',
                              fontWeight: '500'
                            }} size="sm" onClick={() => {
                              if (!window.confirm('Delete ALL connections in this invitation group? This cannot be undone.')) return;
                              group.connections.forEach(c => deleteConnection(c.id));
                            }} disabled={group.connections.some(c => updating[c.id])}>
                                                                        {group.connections.some(c => updating[c.id]) ? <Spinner size="sm" style={{
                                color: minimal.danger
                              }} /> : 'Delete'}
                                                                    </Button>
                                                                    <span onClick={() => toggleGroup(invitationId)} style={{
                              cursor: 'pointer',
                              color: minimal.textSecondary,
                              padding: '5px 10px',
                              backgroundColor: "transparent",
                              borderRadius: '6px',
                              border: `1px solid ${minimal.border}`
                            }}>
                                                                        {expandedGroups[invitationId] ? '▲' : '▼'}
                                                                    </span>
                                                                </Col>
                                                            </Row>
                                                        </div>

                                                        <Collapse isOpen={expandedGroups[invitationId]}>
                                                            <div className="card-body" style={{
                          backgroundColor: "transparent",
                          padding: 0
                        }}>
                                                                {(() => {
                            const selectedInGroup = group.connections.filter(c => selectedConnections[c.id]);
                            const isAllSelectedInGroup = group.connections.length > 0 && selectedInGroup.length === group.connections.length;
                            const hasSelectedInGroup = selectedInGroup.length > 0;
                            return <>
                                                                            {hasSelectedInGroup && <div style={{
                                padding: '10px 20px',
                                backgroundColor: "transparent",
                                borderBottom: `1px solid ${minimal.border}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                              }}>
                                                                                    <span style={{
                                  color: minimal.dangerText,
                                  fontWeight: '600',
                                  fontSize: '0.9rem'
                                }}>
                                                                                        {selectedInGroup.length}{t("connection(s) selected")}</span>
                                                                                    <Button color="danger" size="sm" onClick={() => deleteSelectedConnections(group.connections)} style={{
                                  fontSize: '0.85rem',
                                  fontWeight: '600'
                                }}>{t("Delete Selected")}</Button>
                                                                                </div>}
                                                                            <Table responsive size="sm" style={{
                                color: minimal.textPrimary,
                                marginBottom: 0
                              }}>
                                                                                <thead>
                                                                                    <tr style={{
                                    backgroundColor: "transparent",
                                    color: minimal.textSecondary,
                                    borderBottom: `1px solid ${minimal.border}`
                                  }}>
                                                                                        <th style={{
                                      borderTop: 'none',
                                      padding: '12px 15px',
                                      width: '40px',
                                      textAlign: 'center'
                                    }}>
                                                                                            <input type="checkbox" checked={isAllSelectedInGroup} onChange={e => toggleSelectAllInGroup(group.connections, e.target.checked)} style={{
                                        cursor: 'pointer'
                                      }} />
                                                                                        </th>
                                                                                        <th style={{
                                      borderTop: 'none',
                                      fontWeight: '600',
                                      padding: '12px 15px'
                                    }}>{t("From User")}</th>
                                                                                        <th style={{
                                      borderTop: 'none',
                                      fontWeight: '600',
                                      padding: '12px 15px'
                                    }}>{t("To User")}</th>
                                                                                        <th style={{
                                      borderTop: 'none',
                                      fontWeight: '600',
                                      padding: '12px 15px'
                                    }}>{t("Status")}</th>
                                                                                        <th style={{
                                      borderTop: 'none',
                                      fontWeight: '600',
                                      padding: '12px 15px'
                                    }}>{t("Cards (Inv)")}</th>
                                                                                        <th style={{
                                      borderTop: 'none',
                                      fontWeight: '600',
                                      padding: '12px 15px'
                                    }}>{t("Cards (Res)")}</th>
                                                                                        <th style={{
                                      borderTop: 'none',
                                      fontWeight: '600',
                                      padding: '12px 15px'
                                    }}>{t("Created")}</th>
                                                                                        <th style={{
                                      borderTop: 'none',
                                      fontWeight: '600',
                                      padding: '12px 15px'
                                    }}>{t("Updated")}</th>
                                                                                        <th style={{
                                      borderTop: 'none',
                                      fontWeight: '600',
                                      padding: '12px 15px'
                                    }}>{t("Actions")}</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {group.connections.map(connection => <tr key={connection.id} className={`status-${connection.status}`}>
                                                                                            <td style={{
                                      verticalAlign: 'top',
                                      borderTop: `1px solid ${minimal.border}`,
                                      padding: '12px 15px',
                                      textAlign: 'center'
                                    }}>
                                                                                                <input type="checkbox" checked={!!selectedConnections[connection.id]} onChange={() => toggleSelectConnection(connection.id)} style={{
                                        cursor: 'pointer'
                                      }} />
                                                                                            </td>
                                                                                            <td style={{
                                      verticalAlign: 'top',
                                      borderTop: `1px solid ${minimal.border}`,
                                      padding: '12px 15px'
                                    }}>
                                                                                                <strong style={{
                                        color: minimal.textPrimary,
                                        fontWeight: '600'
                                      }}>
                                                                                                    {connection.fromProfileName || ((connection.fromFirstName || connection.fromLastName) ? `${connection.fromFirstName || ''} ${connection.fromLastName || ''}`.trim() : null) || connection.fromEmail || connection.fromUsername || 'Unknown'}
                                                                                                </strong>
                                                                                                <br />
                                                                                                <small style={{ color: minimal.textSecondary }}>
                                                                                                    {connection.fromEmail || connection.fromUsername}
                                                                                                </small>
                                                                                                {connection.fromPhone && (
                                                                                                    <>
                                                                                                        <br />
                                                                                                        <small style={{ color: minimal.textSecondary }}>
                                                                                                            {connection.fromPhone}
                                                                                                        </small>
                                                                                                    </>
                                                                                                )}
                                                                                                <br />
                                                                                                <small style={{
                                        color: minimal.textSecondary
                                      }}>{t("ID:")}{connection.fromUserId}</small>
                                                                                            </td>
                                                                                            <td style={{
                                      verticalAlign: 'top',
                                      borderTop: `1px solid ${minimal.border}`,
                                      padding: '12px 15px'
                                    }}>
                                                                                                <strong style={{
                                        color: minimal.textPrimary,
                                        fontWeight: '600'
                                      }}>
                                                                                                    {connection.toProfileName || ((connection.toFirstName || connection.toLastName) ? `${connection.toFirstName || ''} ${connection.toLastName || ''}`.trim() : null) || connection.toEmail || connection.toUsername || 'Unknown'}
                                                                                                </strong>
                                                                                                <br />
                                                                                                <small style={{ color: minimal.textSecondary }}>
                                                                                                    {connection.toEmail || connection.toUsername}
                                                                                                </small>
                                                                                                {connection.toPhone && (
                                                                                                    <>
                                                                                                        <br />
                                                                                                        <small style={{ color: minimal.textSecondary }}>
                                                                                                            {connection.toPhone}
                                                                                                        </small>
                                                                                                    </>
                                                                                                )}
                                                                                                <br />
                                                                                                <small style={{
                                        color: minimal.textSecondary
                                      }}>{t("ID:")}{connection.toUserId}</small>
                                                                                            </td>
                                                                                            <td style={{
                                      verticalAlign: 'top',
                                      borderTop: `1px solid ${minimal.border}`,
                                      padding: '12px 15px'
                                    }}>
                                                                                                <span style={getStatusBadgeStyle(connection.status)}>
                                                                                                    {connection.status}
                                                                                                </span>
                                                                                            </td>
                                                                                            <td style={{
                                      verticalAlign: 'top',
                                      borderTop: `1px solid ${minimal.border}`,
                                      padding: '12px 15px'
                                    }}>
                                                                                                <small>
                                                                                                    {(() => {
                                          try {
                                            const cards = typeof connection.connectionCards === 'string' ? JSON.parse(connection.connectionCards) : connection.connectionCards || [];
                                            if (!Array.isArray(cards) || cards.length === 0) return 'N/A';
                                            return cards.map((c, idx) => {
                                              const cardObj = typeof c === 'string' ? {
                                                name: c,
                                                properties: []
                                              } : c || {};
                                              const name = cardObj.name || cardObj.id || 'Unknown';
                                              const props = Array.isArray(cardObj.properties) ? cardObj.properties : [];
                                              return <div key={cardObj.id || idx} style={{
                                                marginBottom: '8px',
                                                padding: '6px',
                                                backgroundColor: 'rgba(255,255,255,0.05)',
                                                borderRadius: '4px'
                                              }}>
                                                                                                                        <div style={{
                                                  fontWeight: 'bold',
                                                  color: minimal.primary
                                                }}>{name}</div>
                                                                                                                        {props.length > 0 ? <div style={{
                                                  fontSize: '0.8rem',
                                                  marginTop: '4px'
                                                }}>
                                                                                                                                {props.map((p, i) => {
                                                    let val = p.value;

                                                    if (!val && p.parameters) {
                                                      if (typeof p.parameters === 'object') {
                                                        if (p.parameters['1'] && p.parameters['1'].value) val = p.parameters['1'].value;else if (p.parameters.email) val = p.parameters.email;else if (p.parameters.phone) val = p.parameters.phone;else if (p.parameters.value) val = p.parameters.value;
                                                      }
                                                    }

                                                    let displayVal = val || 'N/A';
                                                    if (typeof displayVal === 'string' && displayVal.length > 50) {
                                                      displayVal = displayVal.substring(0, 50) + '...';
                                                    }
                                                    return <div key={i} style={{
                                                      color: '#e2e8f0'
                                                    }}>
                                                                                                                                            <span style={{
                                                        color: '#9ca3af',
                                                        marginRight: '4px'
                                                      }}>{p.name}:</span>{displayVal}
                                                                                                                                        </div>;
                                                  })}
                                                                                                                            </div> : <div style={{
                                                  fontSize: '0.75rem',
                                                  color: minimal.textSecondary
                                                }}>{t("No properties")}</div>}
                                                                                                                    </div>;
                                            });
                                          } catch (e) {
                                            return '[]';
                                          }
                                        })()}
                                                                                                </small>
                                                                                            </td>
                                                                                            <td style={{
                                      verticalAlign: 'top',
                                      borderTop: `1px solid ${minimal.border}`,
                                      padding: '12px 15px'
                                    }}>
                                                                                                <small style={{
                                        color: minimal.textSecondary
                                      }}>
                                                                                                    {(() => {
                                          try {
                                            const cards = typeof connection.responseCards === 'string' ? JSON.parse(connection.responseCards) : connection.responseCards || [];
                                            if (!Array.isArray(cards) || cards.length === 0) return 'N/A';
                                            return cards.map((c, idx) => {
                                              const cardObj = typeof c === 'string' ? {
                                                name: c,
                                                properties: []
                                              } : c || {};
                                              const name = cardObj.name || cardObj.id || 'Unknown';
                                              const props = Array.isArray(cardObj.properties) ? cardObj.properties : [];
                                              return <div key={cardObj.id || idx} style={{
                                                marginBottom: '8px',
                                                padding: '6px',
                                                backgroundColor: 'rgba(255,255,255,0.05)',
                                                borderRadius: '4px'
                                              }}>
                                                                                                                        <div style={{
                                                  fontWeight: 'bold',
                                                  color: minimal.primary
                                                }}>{name}</div>
                                                                                                                        {props.length > 0 ? <div style={{
                                                  fontSize: '0.8rem',
                                                  marginTop: '4px'
                                                }}>
                                                                                                                                {props.map((p, i) => {
                                                    let val = p.value;

                                                    if (!val && p.parameters) {
                                                      if (typeof p.parameters === 'object') {
                                                        if (p.parameters['1'] && p.parameters['1'].value) val = p.parameters['1'].value;else if (p.parameters.email) val = p.parameters.email;else if (p.parameters.phone) val = p.parameters.phone;else if (p.parameters.value) val = p.parameters.value;
                                                      }
                                                    }

                                                    let displayVal = val || 'N/A';
                                                    if (typeof displayVal === 'string' && displayVal.length > 50) {
                                                      displayVal = displayVal.substring(0, 50) + '...';
                                                    }
                                                    return <div key={i} style={{
                                                      color: '#e2e8f0'
                                                    }}>
                                                                                                                                            <span style={{
                                                        color: '#9ca3af',
                                                        marginRight: '4px'
                                                      }}>{p.name}:</span>{displayVal}
                                                                                                                                        </div>;
                                                  })}
                                                                                                                            </div> : <div style={{
                                                  fontSize: '0.75rem',
                                                  color: minimal.textSecondary
                                                }}>{t("No properties")}</div>}
                                                                                                                    </div>;
                                            });
                                          } catch (e) {
                                            return '[]';
                                          }
                                        })()}
                                                                                                </small>
                                                                                            </td>
                                                                                            <td style={{
                                      verticalAlign: 'top',
                                      borderTop: `1px solid ${minimal.border}`,
                                      padding: '12px 15px'
                                    }}>
                                                                                                <small style={{
                                        color: minimal.textSecondary
                                      }}>{formatDate(connection.createdAt)}</small>
                                                                                            </td>
                                                                                            <td style={{
                                      verticalAlign: 'top',
                                      borderTop: `1px solid ${minimal.border}`,
                                      padding: '12px 15px'
                                    }}>
                                                                                                <small style={{
                                        color: minimal.textSecondary
                                      }}>{formatDate(connection.updatedAt)}</small>
                                                                                            </td>
                                                                                            <td style={{
                                      verticalAlign: 'top',
                                      borderTop: `1px solid ${minimal.border}`,
                                      padding: '12px 15px'
                                    }}>
                                                                                                <div className="d-flex align-items-center">
                                                                                                    <Input type="select" value={connection.status} onChange={e => updateStatus(connection.id, e.target.value)} disabled={updating[connection.id]} bsSize="sm" style={{
                                          width: '110px',
                                          backgroundColor: "transparent",
                                          color: minimal.textPrimary,
                                          border: `1px solid ${minimal.border}`,
                                          borderRadius: '6px',
                                          fontSize: '0.85rem'
                                        }}>
                                                                                                        <option value="pending">{t("Pending")}</option>
                                                                                                        <option value="connected">{t("Connected")}</option>
                                                                                                        <option value="declined">{t("Declined")}</option>
                                                                                                        <option value="expired">{t("Expired")}</option>
                                                                                                    </Input>
                                                                                                    <Button style={{
                                          backgroundColor: 'transparent',
                                          color: minimal.danger,
                                          border: 'none',
                                          marginLeft: '4px',
                                          padding: '4px 8px'
                                        }} size="sm" onClick={() => deleteConnection(connection.id)} disabled={updating[connection.id]} title={t("Delete")}>
                                                                                                        {updating[connection.id] ? <Spinner size="sm" style={{
                                            color: minimal.danger
                                          }} /> : '×'}
                                                                                                    </Button>
                                                                                                </div>
                                                                                            </td>
                                                                                        </tr>)}
                                                                                </tbody>
                                                                            </Table>
                                                                        </>;
                          })()}
                                                            </div>
                                                        </Collapse>
                                                </Card>)}
                                        </div>}
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </Container>
                    </div>
                </div>
        </AdminLayout>;
};

export default ConnectionsMonitor;